import React, { useCallback, useEffect, useState } from "react";
import "./../styles/product.css";
import ToastNotification, { showToast } from "../components/toasts/ToastNotification";
import { HashLoader } from "react-spinners";
import { getProductBySlug } from "../api/product.api";
import { useNavigate, useParams } from "react-router-dom";
import { getSimilarProducts } from "../api/recommend.api";
import { useUser } from "../context/UserContext";
import { addToCart } from "../api/cart.api";
import ProductHeaderCard from "../components/product/ProductHeaderCard";
import ProductTopupPackages from "../components/product/ProductTopupPackages";
import ProductOrderSummary from "../components/product/ProductOrderSummary";
import ProductReviewSection from "../components/product/ProductReviewSection";
import ProductRecommendationList from "../components/product/ProductRecommendationList";

const REVIEW_LIMIT = 6;

const Product = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orderPrice, setOrderPrice] = useState(0);
  const [orderDiscount] = useState(0);
  const [orderTotal, setOrderTotal] = useState(0);
  const { setCart } = useUser();

  const [recommendations, setRecommendations] = useState(null);
  const [selectedTopUpPackage, setSelectedTopUpPackage] = useState(null);

  const { product_slug } = useParams();
  const navigate = useNavigate();

  const fetchProductData = useCallback(async () => {
    setLoading(true);

    try {
      const res = await getProductBySlug(product_slug, REVIEW_LIMIT);

      if (res.success) {
        setProduct(res.data.product);

        if (
          res.data.product.product_type === "game_account" ||
          res.data.product.product_type === "utility_account" ||
          res.data.product.product_type === "gift_card"
        ) {
          setOrderPrice(res.data.product.product_attributes.price);
        } else if (res.data.product.product_type === "topup_package") {
          const availablePackage = res.data.product.product_attributes.packages.find(
            (pkg) => pkg.status === "available"
          );
          setSelectedTopUpPackage(availablePackage || null);
        }

        document.title = res.data.product.product_name;
        const similarRes = await getSimilarProducts(res.data.product.productId);

        if (similarRes.success) {
          setRecommendations(similarRes.data.recommendations);
        }
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  }, [product_slug]);

  useEffect(() => {
    fetchProductData();

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [fetchProductData]);

  useEffect(() => {
    setOrderTotal(orderPrice - orderDiscount);
  }, [orderPrice, orderDiscount]);

  useEffect(() => {
    setOrderPrice(selectedTopUpPackage?.price || 0);
  }, [selectedTopUpPackage]);

  const handleCheckout = () => {
    const { productId, product_type } = product;

    const queryParams = new URLSearchParams({
      productId,
      product_type,
    });

    if (product_type === "topup_package") {
      if (!selectedTopUpPackage || selectedTopUpPackage.status !== "available") {
        showToast("Gói nạp này hiện không khả dụng", "error");
        return;
      }

      navigate(`/checkout?${queryParams.toString()}`, {
        state: {
          product_package: {
            ...selectedTopUpPackage,
            product_img: product.product_imgs[0],
          },
        },
      });

      return;
    }

    navigate(`/checkout?${queryParams.toString()}`, {
      state: {
        product,
      },
    });
  };

  const handleAddToCart = async () => {
    if (product?.product_type === "topup_package" && selectedTopUpPackage?.status !== "available") {
      showToast("Gói nạp này hiện không khả dụng", "error");
      return;
    }

    try {
      setLoading(true);

      const res = await addToCart(
        product?.productId,
        product?.product_type === "topup_package" ? selectedTopUpPackage?._id : null
      );

      if (res.success) {
        setCart(Number(res?.data?.cartCount || 0));
        showToast("Thêm sản phẩm vào giỏ hàng thành công", "success");
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ height: "100vh" }} className="loader-container">
        <HashLoader color="var(--hash-loader-color)" />
      </div>
    );
  }

  return (
    <div className="pdp-page">
      <div className="pdp-layout">
        <div className="pdp-main-col">
          <ProductHeaderCard product={product} />

          <ProductTopupPackages
            product={product}
            selectedTopUpPackage={selectedTopUpPackage}
            setSelectedTopUpPackage={setSelectedTopUpPackage}
          />

          {isMobile && (
            <ProductOrderSummary
              className="pdp-order-card--mobile"
              orderPrice={orderPrice}
              orderDiscount={orderDiscount}
              orderTotal={orderTotal}
              handleAddToCart={handleAddToCart}
              handleCheckout={handleCheckout}
            />
          )}

          <ProductReviewSection
            product={product}
            productSlug={product_slug}
            navigate={navigate}
          />
        </div>

        <div className="pdp-side-col">
          {!isMobile && (
            <ProductOrderSummary
              orderPrice={orderPrice}
              orderDiscount={orderDiscount}
              orderTotal={orderTotal}
              handleAddToCart={handleAddToCart}
              handleCheckout={handleCheckout}
            />
          )}

          <ProductRecommendationList recommendations={recommendations} navigate={navigate} />
        </div>
      </div>

      <ToastNotification />
    </div>
  );
};

export default Product;
